import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
	Camera,
	RotateCcw,
	SwitchCamera,
	HelpCircle,
	AlertTriangle,
	Loader2,
} from 'lucide-react';
import FacialVerificationGuide from './FacialVerificationGuide';
import { useSelfieFd } from '../hooks/useSelfieFd';
import { resolveCameraAccessErrorMessage } from '../lib/selfie-fd/selfie-fd-engine';

interface CameraCaptureProps {
	onCapture: (file: File) => void;
	disabled?: boolean;
}

const CameraCapture: React.FC<CameraCaptureProps> = ({ onCapture, disabled }) => {
	const [capturedImage, setCapturedImage] = useState<string | null>(null);
	const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
	const [showGuide, setShowGuide] = useState(false);
	const [isCapturing, setIsCapturing] = useState(false);
	const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
	const [wantsStream, setWantsStream] = useState(false);
	const [restartNonce, setRestartNonce] = useState(0);
	const startingRef = useRef(false);

	const {
		videoRef,
		overlayRef,
		isLoading,
		isLoaded,
		error: detectorError,
		isStreaming,
		snapshot,
		canCapture,
		startCamera,
		stopCamera,
		captureSelfie,
		attachElements,
	} = useSelfieFd();

	const isMobile = /Mobi|Android/i.test(navigator.userAgent);

	useEffect(() => {
		if (navigator.mediaDevices?.enumerateDevices) {
			navigator.mediaDevices.enumerateDevices().then((mediaDevices) => {
				setDevices(mediaDevices.filter(({ kind }) => kind === 'videoinput'));
			});
		}
	}, []);

	// Mount video/canvas first, then attach + start once the user asked to stream.
	useEffect(() => {
		if (!wantsStream || startingRef.current || isStreaming) return;

		startingRef.current = true;
		const timer = window.setTimeout(() => {
			attachElements();
			startCamera(facingMode)
				.catch((error) => {
					alert(resolveCameraAccessErrorMessage(error));
					setWantsStream(false);
				})
				.finally(() => {
					startingRef.current = false;
				});
		}, 0);

		return () => window.clearTimeout(timer);
	}, [wantsStream, isStreaming, facingMode, restartNonce, attachElements, startCamera]);

	const handleStartCamera = useCallback(() => {
		setWantsStream(true);
	}, []);

	const handleStopCamera = useCallback(async () => {
		setWantsStream(false);
		await stopCamera();
	}, [stopCamera]);

	const handleCapture = useCallback(async () => {
		if (!canCapture) {
			alert(snapshot.primaryHint || 'Please wait for face validation to complete before capturing');
			return;
		}

		setIsCapturing(true);
		try {
			const file = await captureSelfie();
			const previewUrl = URL.createObjectURL(file);
			onCapture(file);
			setCapturedImage(previewUrl);
			setWantsStream(false);
			await stopCamera();
		} catch (error) {
			console.error('Capture failed:', error);
			alert(error instanceof Error ? error.message : 'Capture failed. Please try again.');
		} finally {
			setIsCapturing(false);
		}
	}, [canCapture, snapshot.primaryHint, captureSelfie, onCapture, stopCamera]);

	const switchCamera = useCallback(async () => {
		const next = facingMode === 'user' ? 'environment' : 'user';
		setFacingMode(next);
		if (wantsStream || isStreaming) {
			await stopCamera();
			startingRef.current = false;
			setRestartNonce((n) => n + 1);
		}
	}, [facingMode, wantsStream, isStreaming, stopCamera]);

	const resetCapture = () => {
		if (capturedImage) {
			URL.revokeObjectURL(capturedImage);
		}
		setCapturedImage(null);
	};

	const showCamera = wantsStream || isStreaming;

	const hintToneClass =
		snapshot.guideTone === 'ready' || snapshot.guideTone === 'progress'
			? 'bg-green-500 bg-opacity-90'
			: snapshot.guideTone === 'warn'
				? 'bg-orange-500 bg-opacity-90'
				: 'bg-gray-800 bg-opacity-80';

	if (capturedImage) {
		return (
			<div className="space-y-4 h-72 md:h-96">
				<div className="relative">
					<img
						src={capturedImage}
						alt="Captured selfie"
						className="w-full h-72 md:h-96 object-cover rounded-lg border-2 border-gray-200"
					/>
					<button
						type="button"
						onClick={resetCapture}
						disabled={disabled}
						className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors disabled:opacity-50"
					>
						<RotateCcw className="w-4 h-4 text-gray-600" />
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<div className="flex justify-between items-center">
				<p className="mb-1">Capture Your Selfie</p>
				<button
					type="button"
					onClick={() => setShowGuide(true)}
					className="flex items-center gap-2 p-4 cursor-pointer text-ghana-red hover:bg-red-50 rounded-lg transition-colors text-sm"
				>
					<HelpCircle className="w-4 h-4" />
					Photo Guidelines
				</button>
			</div>

			{showCamera ? (
				<div className="relative h-72 md:h-96 overflow-hidden rounded-lg border-2 border-gray-200 bg-black">
					<video
						ref={videoRef}
						playsInline
						muted
						autoPlay
						className={`absolute inset-0 w-full h-full object-cover ${
							facingMode === 'user' ? 'scale-x-[-1]' : ''
						}`}
					/>
					<canvas
						ref={overlayRef}
						className="absolute inset-0 w-full h-full pointer-events-none"
						aria-hidden
					/>

					<div
						className={`absolute top-2 left-2 right-2 text-white text-xs px-3 py-2 rounded-lg ${hintToneClass}`}
					>
						<p className="font-medium">{snapshot.primaryHint}</p>
					</div>

					<div className="absolute bottom-4 right-4 flex gap-2">
						{(devices.length > 1 ||
							navigator.mediaDevices?.getSupportedConstraints?.().facingMode) && (
							<button
								type="button"
								onClick={() => void switchCamera()}
								disabled={disabled}
								className="p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors disabled:opacity-50"
								title="Switch Camera"
							>
								<SwitchCamera className="w-6 h-6 text-gray-600" />
							</button>
						)}
					</div>

					<div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-3 text-xs">
						<button
							type="button"
							onClick={() => void handleStopCamera()}
							disabled={disabled}
							className="px-4 py-3 bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-colors disabled:opacity-50"
						>
							Cancel
						</button>
						<button
							type="button"
							onClick={() => void handleCapture()}
							disabled={disabled || !canCapture || isCapturing}
							className="px-4 py-3 max-w-lg bg-ghana-red text-white rounded-full hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
						>
							{isCapturing ? (
								<>
									<Loader2 className="w-4 h-4 animate-spin" />
									Capturing...
								</>
							) : (
								<>
									<Camera className="w-4 h-4" />
									{canCapture ? 'Capture Photo' : 'Position Face'}
								</>
							)}
						</button>
					</div>
				</div>
			) : (
				<div className="flex p-6 rounded-lg flex-col sm:flex-row gap-4 h-72 md:h-96 justify-center items-center w-full border-2 border-dashed border-gray-300">
					<button
						type="button"
						onClick={handleStartCamera}
						disabled={disabled || isLoading || !!detectorError}
						className="hover:border-ghana-gold transition-colors disabled:opacity-50 flex flex-col items-center gap-2"
					>
						{isLoading ? (
							<>
								<Loader2 className="w-8 h-8 text-ghana-gold animate-spin" />
								<span className="text-sm font-medium text-gray-700">Loading face detection</span>
								<span className="text-xs text-gray-500">Please wait...</span>
								{isMobile && (
									<span className="text-xs text-gray-400 mt-1">
										May take longer on mobile networks
									</span>
								)}
							</>
						) : detectorError ? (
							<>
								<AlertTriangle className="w-8 h-8 text-red-500" />
								<span className="text-sm font-medium text-red-600">Failed to Load AI</span>
								<span className="text-xs text-gray-500 text-center max-w-xs">{detectorError}</span>
							</>
						) : (
							<>
								<Camera className="w-8 h-8 text-ghana-gold" />
								<span className="text-sm font-medium text-gray-700">Take Live Selfie</span>
								{isLoaded && isMobile && (
									<span className="text-xs text-gray-500">Tap to start camera</span>
								)}
							</>
						)}
					</button>
				</div>
			)}

			<FacialVerificationGuide isOpen={showGuide} onClose={() => setShowGuide(false)} />
		</div>
	);
};

export default CameraCapture;
